import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, FolderSearch, X, FileText, Loader2 } from 'lucide-react';
import { appsApi, AppUpdate } from '../../lib/api/apps';
import { serversApi } from '../../lib/api/servers';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { toast } from '../../stores/toastStore';
import * as Sentry from '@sentry/react';

const appEditSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().optional(),
  docker_compose_path: z.string().optional(),
  service_name: z.string().optional(),
  env_file_path: z.string().optional(),
  git_repo: z.string().optional(),
  git_branch: z.string().optional(),
  domain: z.string().optional(),
});

type AppEditFormData = z.infer<typeof appEditSchema>;

export function EditAppPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // File browser state
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseFiles, setBrowseFiles] = useState<string[]>([]);
  const [browseError, setBrowseError] = useState<string | null>(null);

  // Services state
  const [services, setServices] = useState<string[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const { data: app, isLoading, error } = useQuery({
    queryKey: ['app', id],
    queryFn: () => appsApi.get(id!),
  });

  const updateApp = useMutation({
    mutationFn: (data: AppUpdate) => appsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app', id] });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      toast.success('App updated successfully');
      navigate(`/apps/${id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to update app';
      toast.error(message);
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<AppEditFormData>({
    resolver: zodResolver(appEditSchema),
    values: app ? {
      name: app.name,
      description: app.description || '',
      docker_compose_path: app.docker_compose_path || '',
      service_name: app.service_name || '',
      env_file_path: app.env_file_path || '',
      git_repo: app.git_repo || '',
      git_branch: app.git_branch || 'main',
      domain: app.domain || '',
    } : undefined,
  });

  const watchComposePath = watch('docker_compose_path');

  // Load services when compose path changes
  useEffect(() => {
    const loadServices = async () => {
      if (app?.server_id && watchComposePath) {
        setServicesLoading(true);
        try {
          const result = await serversApi.getComposeServices(app.server_id, watchComposePath);
          setServices(result.services || []);
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              feature: 'apps',
              operation: 'load_compose_services',
              severity: 'warning',
            },
            extra: {
              appId: id,
              serverId: app?.server_id,
              composePath: watchComposePath,
            },
          });
          setServices([]);
        } finally {
          setServicesLoading(false);
        }
      } else {
        setServices([]);
      }
    };
    loadServices();
  }, [app?.server_id, watchComposePath]);

  // Browse for compose files on server
  const handleBrowseFiles = async () => {
    if (!app?.server_id) return;

    setShowFileBrowser(true);
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const result = await serversApi.browseFiles(app.server_id, '/', '*docker-compose*.yml');
      setBrowseFiles(result.files);
      if (result.error) {
        setBrowseError(result.error);
      }
    } catch (error: any) {
      setBrowseError(error.response?.data?.error?.message || 'Failed to browse files');
    } finally {
      setBrowseLoading(false);
    }
  };

  const handleSelectFile = (filePath: string) => {
    setValue('docker_compose_path', filePath, { shouldDirty: true });
    setShowFileBrowser(false);
  };

  const onSubmit = (data: AppEditFormData) => {
    // Only send fields that have changed
    const updateData: AppUpdate = {};
    if (data.name !== app?.name) updateData.name = data.name;
    if (data.description !== (app?.description || '')) updateData.description = data.description || undefined;
    if (data.docker_compose_path !== (app?.docker_compose_path || '')) updateData.docker_compose_path = data.docker_compose_path || undefined;
    if (data.service_name !== (app?.service_name || '')) updateData.service_name = data.service_name || undefined;
    if (data.env_file_path !== (app?.env_file_path || '')) updateData.env_file_path = data.env_file_path || undefined;
    if (data.git_repo !== (app?.git_repo || '')) updateData.git_repo = data.git_repo || undefined;
    if (data.git_branch !== (app?.git_branch || 'main')) updateData.git_branch = data.git_branch || undefined;
    if (data.domain !== (app?.domain || '')) updateData.domain = data.domain || undefined;

    updateApp.mutate(updateData);
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-red-600">App not found</p>
        <Button variant="link" onClick={() => navigate('/apps')}>
          Back to Apps
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/apps/${id}`)}
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to App
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Edit App: {app.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Name"
              placeholder="My App"
              error={errors.name?.message}
              {...register('name')}
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Slug (read-only)</label>
              <div className="flex w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 font-mono">
                {app.slug}
              </div>
              <p className="text-xs text-gray-500">Slug cannot be changed after creation</p>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                placeholder="Application description..."
                {...register('description')}
              />
            </div>

            {/* Docker Compose Path with Browse */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Docker Compose Path
              </label>
              <div className="flex gap-2">
                <input
                  className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="/home/ubuntu/app/docker-compose.yml"
                  {...register('docker_compose_path')}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBrowseFiles}
                  title="Browse for compose files"
                >
                  <FolderSearch className="h-4 w-4" />
                </Button>
              </div>
              {watchComposePath && (
                <p className="text-xs text-gray-500 mt-1">
                  {servicesLoading ? 'Loading services...' : services.length > 0 ? `Found ${services.length} service(s)` : 'No services found'}
                </p>
              )}
            </div>

            {/* File Browser Modal */}
            {showFileBrowser && (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Select Docker Compose File</h4>
                  <Button size="sm" variant="ghost" onClick={() => setShowFileBrowser(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {browseLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching for docker-compose files...
                  </div>
                ) : browseError ? (
                  <p className="text-red-600">{browseError}</p>
                ) : browseFiles.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {browseFiles.map((file) => (
                      <button
                        key={file}
                        type="button"
                        onClick={() => handleSelectFile(file)}
                        className="w-full text-left p-2 rounded hover:bg-gray-100 text-sm font-mono text-gray-700 flex items-center gap-2"
                      >
                        <FileText className="h-4 w-4 text-gray-400" />
                        {file}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No docker-compose files found</p>
                )}
              </div>
            )}

            {/* Service Name - dropdown when services available */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Service Name
              </label>
              {services.length > 0 ? (
                <select
                  className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  {...register('service_name')}
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="web"
                  {...register('service_name')}
                />
              )}
              {servicesLoading && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading services...
                </p>
              )}
            </div>

            <Input
              label="Env File Path"
              placeholder="/home/ubuntu/app/.env"
              {...register('env_file_path')}
            />
            <p className="text-xs text-gray-500 -mt-3">
              Path to the .env file on the server. Used by "Sync to Server" feature.
            </p>

            <Input
              label="Git Repository"
              placeholder="https://github.com/user/repo.git"
              {...register('git_repo')}
            />

            <Input
              label="Git Branch"
              placeholder="main"
              {...register('git_branch')}
            />

            <Input
              label="Domain"
              placeholder="app.example.com"
              {...register('domain')}
            />

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/apps/${id}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={updateApp.isPending}
                disabled={!isDirty}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
